import { Box, Card, CardBody, Collapse, Flex, Icon, Stack, Tag, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';
import { useAssetsBySIds } from '@/cip/api/asset';
import { getAllocationTypeLabel } from 'utils';

import { findDetailJson, type TradeDetailRecord } from './tradeDetailsModel';

/** One row of the account/portfolio growth-allocation input detail. Custom asset
 * allocations arrive as one row per constituent asset (sId set), other types as
 * a single row per allocation type. */
interface GrowthAllocationInputRecord {
    allocationType?: string;
    targetWeight?: number;
    sId?: string | null;
}

interface GrowthAllocationInput {
    level: 'Account' | 'Portfolio';
    rows: GrowthAllocationInputRecord[];
}

interface AllocationSlice {
    allocationType: string;
    label: string;
    targetWeight: number;
    assets: { sId: string; targetWeight: number }[];
}

export const getGrowthAllocationInput = (details: TradeDetailRecord[]): GrowthAllocationInput | undefined => {
    const accountRows = findDetailJson<GrowthAllocationInputRecord>(
        details,
        TradePlanDetailType.ACCOUNT_GROWTH_ALLOCATION_INPUT,
    );
    const portfolioRows = findDetailJson<GrowthAllocationInputRecord>(
        details,
        TradePlanDetailType.PORTFOLIO_GROWTH_ALLOCATION_INPUT,
    );

    // The account frame is stored for every account and carries the per-asset custom_asset rows,
    // so prefer it; the portfolio frame's presence records that the group is configured
    // portfolio-level. Older optimizations only stored one of the two frames per account.
    const rows = accountRows.length > 0 ? accountRows : portfolioRows;
    if (rows.length === 0) {
        return undefined;
    }
    return { level: portfolioRows.length > 0 ? 'Portfolio' : 'Account', rows };
};

export const hasGrowthAllocationInput = (details: TradeDetailRecord[]): boolean =>
    getGrowthAllocationInput(details) !== undefined;

const CHART_COLORS = [
    '#319795', // teal.500
    '#3182ce', // blue.500
    '#805ad5', // purple.500
    '#d69e2e', // yellow.500
    '#dd6b20', // orange.500
    '#38a169', // green.500
    '#e53e3e', // red.500
    '#00b5d8', // cyan.500
    '#b83280', // pink.500
    '#718096', // gray.500
];

const getColor = (index: number) => CHART_COLORS[index % CHART_COLORS.length];

const formatWeight = (weight: number) => `${(weight * 100).toFixed(2)}%`;

export const toAllocationSlices = (rows: GrowthAllocationInputRecord[]): AllocationSlice[] => {
    const byType = new Map<string, AllocationSlice>();
    for (const row of rows) {
        if (!row.allocationType) continue;

        const slice = byType.get(row.allocationType) ?? {
            allocationType: row.allocationType,
            label: getAllocationTypeLabel(row.allocationType),
            targetWeight: 0,
            assets: [],
        };
        slice.targetWeight += row.targetWeight ?? 0;
        if (row.sId) {
            slice.assets.push({ sId: row.sId, targetWeight: row.targetWeight ?? 0 });
        }
        byType.set(row.allocationType, slice);
    }

    const slices = [...byType.values()].sort((a, b) => b.targetWeight - a.targetWeight);
    slices.forEach((slice) => slice.assets.sort((a, b) => b.targetWeight - a.targetWeight));
    return slices;
};

const ChartTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0];
    return (
        <Box bg="gray.700" borderRadius="md" px={3} py={2} boxShadow="md" fontSize="sm">
            <Text fontWeight="bold">{entry.name}</Text>
            <Text>{formatWeight(entry.value)}</Text>
        </Box>
    );
};

export const GrowthAllocationInputChart = ({ details }: { details: TradeDetailRecord[] }) => {
    const input = useMemo(() => getGrowthAllocationInput(details), [details]);
    const allocations = useMemo(() => toAllocationSlices(input?.rows ?? []), [input]);
    const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});

    // Allocations with assets start expanded; an explicit entry overrides the default
    const toggleExpanded = (allocationType: string) =>
        setExpandedTypes((prev) => ({ ...prev, [allocationType]: !(prev[allocationType] ?? true) }));

    const sIds = useMemo(() => allocations.flatMap((slice) => slice.assets.map((asset) => asset.sId)), [allocations]);
    const { data: assetsBySId } = useAssetsBySIds(sIds);

    const assetLabel = (sId: string) => assetsBySId?.[sId]?.symbol ?? sId;

    // One segment per allocation type; custom asset allocations are split into
    // their constituent assets, shown as shades of the allocation's color.
    const segments = allocations.flatMap((slice, index) =>
        slice.assets.length > 0
            ? slice.assets.map((asset, assetIndex) => ({
                  name: `${slice.label} · ${assetLabel(asset.sId)}`,
                  value: asset.targetWeight,
                  fill: getColor(index),
                  fillOpacity: 1 - Math.min(assetIndex * 0.12, 0.6),
              }))
            : [
                  {
                      name: slice.label,
                      value: slice.targetWeight,
                      fill: getColor(index),
                      fillOpacity: 1,
                  },
              ],
    );

    if (!input) return null;

    return (
        <Card size="sm" w="100%">
            <CardBody>
                <Stack gap={2}>
                    <Flex align="center" gap={2}>
                        <Text fontSize="lg" fontWeight="bold">
                            Target Allocations
                        </Text>
                        <Tag size="sm" colorScheme="teal">
                            {input.level} level
                        </Tag>
                    </Flex>

                    <Flex gap={4} align="flex-start">
                        <Box boxSize="220px" flexShrink={0}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={segments}
                                        dataKey="value"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="55%"
                                        outerRadius="90%"
                                        startAngle={90}
                                        endAngle={-270}
                                        stroke="none"
                                        isAnimationActive={false}
                                    >
                                        {segments.map((entry, index) => (
                                            <Cell
                                                key={`segment-${index}`}
                                                fill={entry.fill}
                                                fillOpacity={entry.fillOpacity}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>

                        <Stack gap={1} flex={1} minW={0}>
                            {allocations.map((slice, index) => {
                                const isExpandable = slice.assets.length > 0;
                                const isExpanded = expandedTypes[slice.allocationType] ?? true;

                                return (
                                    <Box key={slice.allocationType}>
                                        <Flex
                                            align="center"
                                            gap={2}
                                            cursor={isExpandable ? 'pointer' : undefined}
                                            onClick={
                                                isExpandable ? () => toggleExpanded(slice.allocationType) : undefined
                                            }
                                        >
                                            {isExpandable ? (
                                                <Icon
                                                    as={isExpanded ? BsChevronDown : BsChevronRight}
                                                    boxSize={3}
                                                    color="gray.400"
                                                    flexShrink={0}
                                                />
                                            ) : (
                                                <Box boxSize={3} flexShrink={0} />
                                            )}
                                            <Box boxSize={3} borderRadius="sm" bg={getColor(index)} flexShrink={0} />
                                            <Text fontSize="sm" flex={1}>
                                                {slice.label}
                                            </Text>
                                            <Text fontSize="sm" fontWeight="bold">
                                                {formatWeight(slice.targetWeight)}
                                            </Text>
                                        </Flex>
                                        {isExpandable && (
                                            <Collapse in={isExpanded} unmountOnExit>
                                                <Stack gap={0} pt={1}>
                                                    {slice.assets.map((asset) => (
                                                        <Flex key={asset.sId} align="center" gap={2} pl={10}>
                                                            <Text fontSize="xs" color="gray.400" flex={1} noOfLines={1}>
                                                                {assetLabel(asset.sId)}
                                                                {assetsBySId?.[asset.sId]?.description
                                                                    ? ` — ${assetsBySId[asset.sId].description}`
                                                                    : ''}
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.400">
                                                                {formatWeight(asset.targetWeight)}
                                                            </Text>
                                                        </Flex>
                                                    ))}
                                                </Stack>
                                            </Collapse>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Flex>
                </Stack>
            </CardBody>
        </Card>
    );
};
